// ../consent/InformedConsentModal.js
import React, { useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography,
    Divider,
    CircularProgress,
    Alert,
} from "@mui/material";

function formatDateOnly(d) {
    if (!d) return null;
    if (typeof d === "string") return d.slice(0, 10);
    try {
        return new Date(d).toISOString().slice(0, 10);
    } catch {
        return String(d).slice(0, 10);
    }
}

function safeFilePart(s) {
    return String(s || "consent")
        .replace(/[^\w\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 80) || "consent";
}

const LETTER_W_PX = 816;

/**
 * Browser-friendly modal content only.
 * The PDF is created by the backend route:
 * POST /consent/pdf/informed-consent
 */
export function InformedConsentPdfContent({
                                              patientOpt,
                                              signedOn,
                                              patient,
                                              proxy,
                                              signatureSrc,
                                              onSignatureError,
                                              consentMethod,
                                          }) {
    const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
    const dateToShow = formatDateOnly(signedOn) || today;

    const hideBhiLanguage = new Date(`${dateToShow}T00:00:00`) < new Date("2025-01-01T00:00:00");

    const serviceList = hideBhiLanguage
        ? "Chronic Care Management Services (CCM) and/or Remote Physiologic Monitoring (RPM)"
        : "Chronic Care Management Services (CCM), Remote Physiologic Monitoring (RPM), and/or Behavioral Health Integration (BHI)";

    const isOptIn = patientOpt === "optin";
    const isOptOut = patientOpt === "optout";

    const patientName = patient?.name || "(name of patient)";
    const hasProxy = !!proxy?.name || !!proxy?.phone || !!proxy?.email || !!proxy?.relationship;
    const consentMethodToShow = consentMethod || "Electronic";

    return (
        <Box sx={{ color: "#111111", bgcolor: "#ffffff" }}>
            <Box
                component="img"
                src="/2023 Tapestry Logo-400x80.png"
                alt="TapestryHealth"
                sx={{ height: 48, width: "auto", display: "inline-block", mb: 2 }}
            />

            <Typography variant="body2" sx={{ lineHeight: 1.7, color: "#111111" }}>
                Your facility has contracted with TapestryHealth to provide Communication Technology-Based Services (CTBS), such as{" "}
                {serviceList} to residents. These services provide clinical information and insights to help your care team. It is important that you
                understand:
            </Typography>

            <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7, color: "#111111" }}>
                <li>Only one practitioner can provide you with similar CTBS services at a time.</li>
                <li>This does not replace your relationship with your primary care provider or other specialists.</li>
                <li>You can stop these services at any time.</li>
                <li>Cost sharing, such as co-pays or deductibles, is possible depending upon your insurance.</li>
            </Box>

            <Typography variant="body2" sx={{ lineHeight: 1.7, color: "#111111" }}>
                By signing this document,
            </Typography>

            <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7, color: "#111111" }}>
                <li>
                    I consent to allowing TapestryHealth to provide monthly services offered by this facility, which may include {serviceList}.
                </li>
                <li>
                    I acknowledge that only one provider can furnish and bill for TapestryHealth services for me during a calendar month. I will tell you if I enter into a similar agreement with another practice.
                </li>
                <li>
                    I understand that I may receive an Explanation of Benefits (EOB) from my insurance and/or a statement from TapestryHealth for co-pays or co-insurance for these services. The EOB and statements will show Tapestry Telehealth and/or Chess Medicine as the provider. I may be billed for these services up to once a month.
                </li>
                <li>
                    I understand services rendered are dependent on my insurance coverage and any co-pays or co-insurance are determined by my insurance provider.
                </li>
                <li>
                    I consent to electronic communication of my health information with other health care providers involved in my care as allowed by state and local rules and regulations.
                </li>
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 2, color: "#111111" }}>
                Your Rights
            </Typography>

            <Box component="ul" sx={{ mt: 1, mb: 2, pl: 3, lineHeight: 1.7, color: "#111111" }}>
                <li>
                    As part of these services, a copy of your comprehensive plan of care is accessible electronically and provided to your caregivers.
                </li>
                <li>
                    You have the right to stop {serviceList} services at any time, effective at the end of a calendar month. You can opt out of these services at any time
                    by emailing TapestryHealth’s care management team at{" "}
                    <a href="mailto:consent@tapestryhealth.com">consent@tapestryhealth.com</a>.
                </li>
            </Box>

            <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ color: "#111111" }}>
                    <strong>Consent Method:</strong> {consentMethodToShow}
                </Typography>
            </Box>

            <Box
                sx={{
                    mt: 2,
                    color: "#111111",
                    "& .MuiFormControlLabel-label": { fontSize: 14, lineHeight: 1.4, color: "#111111" },
                    "& .MuiFormControlLabel-root.Mui-disabled": { color: "#111111", opacity: 1 },
                    "& .MuiFormControlLabel-root.Mui-disabled .MuiFormControlLabel-label": { color: "#111111", opacity: 1 },
                    "& .MuiCheckbox-root": { color: "#111111" },
                    "& .MuiCheckbox-root.Mui-checked": { color: "#111111" },
                    "& .MuiCheckbox-root.Mui-disabled": { color: "#111111", opacity: 1 },
                    "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": { color: "#111111", opacity: 1 },
                }}
            >
                <FormControlLabel
                    control={<Checkbox checked={isOptIn} disabled />}
                    label="I have read and understood the information and consent to the TapestryHealth services offered by this facility as described above. I am aware that this consent is valid unless I withdraw my consent."
                />
                <FormControlLabel
                    control={<Checkbox checked={isOptOut} disabled />}
                    label="I do not consent to these services."
                />
            </Box>

            <Box sx={{ mt: 3, color: "#111111" }}>
                {!signatureSrc ? (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#111111" }}>
                        Signature of Patient or Authorized Person
                    </Typography>
                ) : (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: "#111111" }}>
                            Signature
                        </Typography>
                        <img
                            src={signatureSrc}
                            alt="signature"
                            style={{
                                maxWidth: "100%",
                                maxHeight: 110,
                                border: "1px solid #d1d5db",
                                borderRadius: 8,
                                padding: 8,
                                background: "white",
                            }}
                            onError={onSignatureError}
                        />
                    </Box>
                )}

                <Typography variant="body2" sx={{ mt: 1, color: "#111111" }}>
                    <strong>Date:</strong> {dateToShow}
                </Typography>
            </Box>

            <Divider sx={{ my: 2, borderColor: "#d1d5db" }} />

            <Box sx={{ mt: 1, color: "#111111" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: "#111111" }}>
                    Patient Information
                </Typography>

                <Typography variant="body2" sx={{ color: "#111111" }}>
                    <strong>Name:</strong> {patientName}
                </Typography>

                {patient?.dob && (
                    <Typography variant="body2" sx={{ color: "#111111" }}>
                        <strong>Date of Birth:</strong> {patient.dob}
                    </Typography>
                )}

                {patient?.facility_name && (
                    <Typography variant="body2" sx={{ color: "#111111" }}>
                        <strong>Facility:</strong> {patient.facility_name}
                    </Typography>
                )}

                {patient?.provider_number && (
                    <Typography variant="body2" sx={{ color: "#111111" }}>
                        <strong>Provider #:</strong> {patient.provider_number}
                    </Typography>
                )}

                {hasProxy && (
                    <>
                        <Divider sx={{ my: 2, borderColor: "#d1d5db" }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: "#111111" }}>
                            Proxy Information
                        </Typography>
                        {proxy?.name && (
                            <Typography variant="body2" sx={{ color: "#111111" }}>
                                <strong>Name:</strong> {proxy.name}
                            </Typography>
                        )}
                        {proxy?.email && (
                            <Typography variant="body2" sx={{ color: "#111111" }}>
                                <strong>Email:</strong> {proxy.email}
                            </Typography>
                        )}
                        {proxy?.phone && (
                            <Typography variant="body2" sx={{ color: "#111111" }}>
                                <strong>Phone:</strong> {proxy.phone}
                            </Typography>
                        )}
                        {proxy?.relationship && (
                            <Typography variant="body2" sx={{ color: "#111111" }}>
                                <strong>Relationship:</strong> {proxy.relationship}
                            </Typography>
                        )}
                    </>
                )}

                <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 2, textAlign: "center", color: "#6b7280" }}
                >
                    A copy of this signed consent will be provided to TapestryHealth.
                </Typography>

                <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 2, textAlign: "center", color: "#111111" }}
                >
                    www.tapestryhealth.com | updated March 2026
                </Typography>
            </Box>
        </Box>
    );
}

export default function InformedConsentModal({
                                                 open,
                                                 onClose,
                                                 patientOpt,
                                                 signedOn,
                                                 patient,
                                                 proxy,
                                                 signatureSrc,
                                                 consentMethod,
                                                 autoGeneratePdf = false,
                                             }) {
    const [pdfBusy, setPdfBusy] = useState(false);
    const [pdfError, setPdfError] = useState("");
    const [sigFailed, setSigFailed] = useState(false);

    const patientName = patient?.name || "(name of patient)";

    const generatePdf = async () => {
        setPdfError("");
        setPdfBusy(true);

        try {
            const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

            if (!apiBase) {
                throw new Error("Missing REACT_APP_API_URL.");
            }

            const resp = await fetch(`${apiBase}/consent/pdf/informed-consent`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    patientOpt,
                    signedOn,
                    patient,
                    proxy,
                    signatureDataUrl: sigFailed ? "" : (signatureSrc || ""),
                    consentMethod: consentMethod || "Electronic",
                }),
            });

            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                throw new Error(errData?.error || "Failed to create PDF.");
            }

            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);

            const safeName = safeFilePart(patientName || "consent");
            const dateOnly = formatDateOnly(signedOn) || new Date().toISOString().slice(0, 10);
            const fileName = `Informed-Consent-${safeName}-${dateOnly}.pdf`;

            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();

            URL.revokeObjectURL(url);
        } catch (e) {
            setPdfError(e?.message || "Failed to generate PDF.");
        } finally {
            setPdfBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Informed Consent</DialogTitle>

            <DialogContent dividers>
                {pdfBusy && (
                    <Alert severity="info" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={16} /> Generating PDF…
                    </Alert>
                )}
                {pdfError && <Alert severity="error" sx={{ mb: 2 }}>{pdfError}</Alert>}

                <Box
                    sx={{
                        width: LETTER_W_PX,
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        p: 3,
                        bgcolor: "white",
                        color: "black",
                        overflow: "visible",
                        height: "auto",
                    }}
                >
                    <InformedConsentPdfContent
                        patientOpt={patientOpt}
                        signedOn={signedOn}
                        patient={patient}
                        proxy={proxy}
                        signatureSrc={sigFailed ? null : signatureSrc}
                        onSignatureError={() => setSigFailed(true)}
                        consentMethod={consentMethod || "Electronic"}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={generatePdf} variant="outlined" disabled={pdfBusy}>
                    {pdfBusy ? "Generating..." : "Download PDF"}
                </Button>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
